import { Grid } from '@mui/material';
import { RoutalPalette, RoutalToggle } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import More3Points from '../../../../assets/svg/More3Points';
import { EditIcon } from '../../../../assets/svg/Plan/MenuBar/EditIcon';
import { OverviewIcon } from '../../../../assets/svg/Plan/MenuBar/OverviewIcon';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import { StopRightPanel } from '../../shared/right-panel/stop-right-panel/StopRightPanel';
import { DriverRightPanel } from '../driver-right-panel/DriverRightPanel';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useSetActivePlanSelectedStops } from '../hooks/plan-details/useSetActivePlanSelectedStops';
import { PlanStopsActionsMenu } from '../menus/PlanStopsActionsMenu';
import { PlanGeoFenceRightPanel } from '../plan-geo-fence-right-panel/PlanGeoFenceRightPanel';
import { EDIT_COLUMN_WIDTH, PlanOverviewEdit } from './plan-overview-leftbar/plan-overview-edit';
import { PlanOverviewList } from './plan-overview-leftbar/plan-overview-list';
import { LIST_COLUMN_WIDTH } from './plan-overview-leftbar/plan-overview-list/PlanOverviewListStyles';
import { PlanOverviewMap } from './plan-overview-map';
import { PlanOptimizeButton } from './PlanOptimizeButton';

export const Overview = () => {
  const { detailType } = useRetrieveRightPanelInfo();
  const { data: plan } = useRetrieveActivePlan();
  const { data: geoFences } = useRetrieveActiveProjectGeoFences();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { selectedStops, setSelectedStops } = useSetActivePlanSelectedStops();
  const [overviewMode, setOverviewMode] = useState<`list` | `edit`>(`list`);
  const [focusedStop, setFocusedStop] = useState<Stop | undefined>(undefined);
  const [overviewWidth, setOverviewWidth] = useState<number>(LIST_COLUMN_WIDTH);

  return (
    <>
      <Grid container wrap="nowrap" style={{ width: `100%`, height: `calc(100% - 38px)` }}>
        <div style={{ minWidth: `${overviewWidth}px` }}>
          {overviewMode === `list` ? (
            <PlanOverviewList selectedStops={selectedStops} onSelectStops={setSelectedStops} />
          ) : (
            <PlanOverviewEdit selectedStops={selectedStops} onSelectStops={setSelectedStops} />
          )}
        </div>

        <PlanOverviewMap focusedStop={focusedStop} setFocusedStop={setFocusedStop} />

        {detailType === `stop` ? (
          <StopRightPanel
            selectedStopsIds={selectedStops}
            validatePlan
            plan={plan}
            planDrivers={drivers}
            planGeoFences={geoFences}
            stopActionsMenu={
              <PlanStopsActionsMenu
                type="popover"
                stops={[]}
                showOpenCustomers={userCredentials?.subscription.test(`customers`)}
                popoverProps={{
                  hideArrow: true,
                  content: <More3Points />,
                  style: {
                    color: RoutalPalette.secondary60,
                    padding: 0,
                    margin: 0,
                    border: `unset`,
                    backgroundColor: RoutalPalette.neutral00,
                  },
                }}
              />
            }
          />
        ) : null}

        {detailType === `driver` ? <DriverRightPanel /> : null}

        {detailType === `geoFence` ? <PlanGeoFenceRightPanel /> : null}
      </Grid>
      <Grid
        style={{
          position: `absolute`,
          zIndex: 1,
          bottom: 18,
          left: 44,
          height: 40,
          width: overviewWidth,
          display: `flex`,
          flexDirection: `row`,
          alignItems: `center`,
          justifyContent: `space-between`,
          padding: `0px 14px`,
        }}
      >
        <RoutalToggle
          onChange={(option) => {
            if (option === `left`) {
              setOverviewMode(`list`);
              setOverviewWidth(LIST_COLUMN_WIDTH);
            }
            if (option === `right`) {
              setOverviewMode(`edit`);
              setOverviewWidth(EDIT_COLUMN_WIDTH * ((drivers ?? []).length > 0 ? 2 : 1));
            }
          }}
          value={overviewMode === `list` ? `left` : `right`}
          containerStyles={{
            width: `fit-content`,
            boxShadow: `0px 0px 4px 0px rgba(0, 0, 0, 0.25)`,
          }}
          leftChild={
            <OverviewIcon color={overviewMode === `list` ? RoutalPalette.neutral00 : RoutalPalette.secondary60} />
          }
          rightChild={
            <EditIcon color={overviewMode === `edit` ? RoutalPalette.neutral00 : RoutalPalette.secondary60} />
          }
        />

        <PlanOptimizeButton />
      </Grid>
    </>
  );
};
